import { useApolloClient } from '@apollo/client';
import { useFormikContext } from 'formik';
import {
  ButtonBack,
  FieldInput,
  FormCard,
  FormCol4,
  FormRow,
  useIsFieldRequiredHook,
  YupDate,
  YupNumeric,
  YupNumericReq,
  YupObject,
  YupString,
  FieldDatepicker,
} from 'motion-components';
import React from 'react';
import { Modal, ModalProps } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createModal } from 'react-modal-promise';
import { useHistory, useLocation } from 'react-router-dom';
import { useHandleError } from 'shared-pages/services';
import { useRootStore } from 'store';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import {
  CompanyVatTitle,
  isAfisCyprus,
  isMedicycle,
  requiredString,
} from 'shared-pages/common/constants';

import {
  FieldSelectWasteReceptionPoint,
  FieldSelectWasteDestinationType,
  FieldSelectRegion,
} from 'shared-pages/components';
import { getWasteReceptionPointById } from 'shared-pages/pages/idocs/graphql';

export class FormInput {
  finaldestinationpointId: number;
  finaldestinationCompanyName: string;
  finaldestinationCompanyAddress: string;
  finaldestinationCompanyPhoneNumber: string;
  finaldestinationCompanyVat: string;
  finaldestinationCompanyTaxOffice: string;
  finaldestinationCompanyImaCode: string;
  finaldestinationCompanyEmail: string;
  finaldestinationCompanyRegionId: number;
  finaldestinationCompanySubregionId: number;
  finaldestinationCompanyNoteDocNumber: string;
  finalDestinationPersonName: string;
  finalDestinationSignFileId: number;
  finalDestinationDate: Date;
  finalDestinationNetWeight: number;

  constructor() {
    this.finaldestinationpointId = undefined;
    this.finaldestinationCompanyName = undefined;
    this.finaldestinationCompanyAddress = undefined;
    this.finaldestinationCompanyPhoneNumber = undefined;
    this.finaldestinationCompanyVat = undefined;
    this.finaldestinationCompanyTaxOffice = undefined;
    this.finaldestinationCompanyImaCode = undefined;
    this.finaldestinationCompanyEmail = undefined;
    this.finaldestinationCompanyRegionId = undefined;
    this.finaldestinationCompanySubregionId = undefined;
    this.finaldestinationCompanyNoteDocNumber = undefined;
    this.finalDestinationPersonName = undefined;
    this.finalDestinationSignFileId = undefined;
    this.finalDestinationDate = undefined;
    this.finalDestinationNetWeight = undefined;
  }
}

function useCustomFormShape(base: any) {
  return Yup.object(base);
}

const FormPage = ({ finalDestinationNetWeight, modalOnClose }) => {
  const { handleGraphqlError } = useHandleError('collector');
  const { appStore, authStore } = useRootStore();
  const history = useHistory();
  const location: any = useLocation();

  const { t, i18n } = useTranslation();
  const client = useApolloClient();

  const formShape = {
    finaldestinationpointId: YupNumericReq,
    finaldestinationCompanyName: YupString,
    finaldestinationCompanyAddress: YupString,
    finaldestinationCompanyPhoneNumber: YupString,
    finaldestinationCompanyVat: YupString,
    finaldestinationCompanyTaxOffice: YupString,
    finaldestinationCompanyImaCode: YupString,
    finaldestinationCompanyEmail: YupString,
    finaldestinationCompanyRegionId: YupNumeric,
    finaldestinationCompanySubregionId: YupNumeric,
    finaldestinationCompanyNoteDocNumber: YupString,
    finalDestinationPersonName: YupString,
    finalDestinationSignFileId: YupNumeric,
    finalDestinationDate: YupDate.required(requiredString),
    finalDestinationNetWeight: YupNumeric.required(requiredString),
    finalDestinationItransId: YupNumeric,
    finalDestinationITrans: YupObject,
  };

  const validationSchema = useCustomFormShape(formShape);

  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
  type FormValuesType = Yup.InferType<typeof validationSchema>;

  const handleSubmit = async (values: FormValuesType, actions) => {
    actions.setSubmitting(false);

    const mutValues = Lo.pick(values, Lo.keys(new FormInput()));

    modalOnClose(mutValues);
  };

  return (
    <FormCard
      initialValues={{ finalDestinationNetWeight }}
      title="Τελική Διάθεση Αποβλήτων"
      injectHeaderElements={() => <ButtonBack onClick={() => modalOnClose(-1)}></ButtonBack>}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      <FormFields validationSchema={validationSchema} />
    </FormCard>
  );
};

export const FormFields = ({ validationSchema }) => {
  const data = {};

  const { t } = useTranslation();
  const client = useApolloClient();
  const isFieldRequired = useIsFieldRequiredHook(validationSchema);

  const { values, setFieldValue, errors, isValid, touched } = useFormikContext<any>();

  /** Συμπλήρωση πεδίων τελικού αποδέκτη από το επιλεγμένο σημείο παραλαβής */
  async function onFinaldestinationpointChange(e) {
    if (e.value === null) return;
    if (values.finaldestinationpointId === e.value) return;

    const id = parseInt(e.value);
    const rec: any = await getWasteReceptionPointById(client, id);
    if (!rec) return;

    setFieldValue('finaldestinationpointId', id);
    setFieldValue('finaldestinationpoint.wasteDestinationTypeId', rec.wasteDestinationTypeId);
    setFieldValue('finaldestinationCompanyName', rec.name);
    setFieldValue('finaldestinationCompanyAddress', rec.addressLine1);
    setFieldValue('finaldestinationCompanyPhoneNumber', rec.phone);
    !isAfisCyprus && setFieldValue('finaldestinationCompanyVat', rec.companyVat);
    !isAfisCyprus && setFieldValue('finaldestinationCompanyTaxOffice', rec.companyTaxOffice);
    setFieldValue('finaldestinationCompanyImaCode', rec.imaCode ?? rec.companyImaCode ?? '');
    setFieldValue('finaldestinationCompanyRegionId', rec.regionId ?? null);
    setFieldValue('finaldestinationCompanySubregionId', rec.subregionId ?? null);
    setFieldValue('finaldestinationCompanyEmail', rec.email ?? rec.companyEmail ?? '');
    setFieldValue('finalDestinationPersonName', rec.receptionPersonName ?? '');
  }

  const TextField = ({ label, name }) => {
    return <FieldInput disabled={true} as={FormCol4} label={label} name={name} />;
  };

  return (
    <>
      <h5 className="form-group-title">4. {t('Τελικός Αποδέκτης')}</h5>
      <FormRow>
        <FieldSelectWasteReceptionPoint
          name="finaldestinationpointId"
          required={isFieldRequired('finaldestinationpointId', values)}
          as={FormCol4}
          defaultValues={data}
          onChange={onFinaldestinationpointChange}
        />
        <FieldDatepicker
          name="finalDestinationDate"
          required={values?.isCancelled ? false : isFieldRequired('finalDestinationDate', values)}
          as={FormCol4}
          label="Ημ/νία & Ώρα Παράδοσης στον Τελικό Αποδέκτη"
          hasMask={true}
          placeholder={'dd/mm/yyyy HH:mm'}
          showTimeSelect
          dateFormat="d/M/yyyy HH:mm"
          timeFormat="HH:mm"
          timeIntervals={15}
        />
      </FormRow>
      <FormRow>
        <FieldSelectWasteDestinationType
          name="finaldestinationpoint.wasteDestinationTypeId"
          required={false}
          disabled={true}
          as={FormCol4}
          label={t('Κατηγορία Τελικού Αποδέκτη')}
          defaultValues={data}
        />
        <TextField label="Διεύθυνση" name="finaldestinationCompanyAddress" />
        <TextField label="ΑΜ. ΗΜΑ" name="finaldestinationCompanyImaCode" />
      </FormRow>
      <FormRow>
        <FieldInput
          name="finaldestinationCompanyPhoneNumber"
          required={
            values?.isCancelled
              ? false
              : isFieldRequired('finaldestinationCompanyPhoneNumber', values)
          }
          as={FormCol4}
          label="Τηλέφωνο"
        />
        <FieldSelectRegion
          name={'finaldestinationCompanyRegionId'}
          required={isFieldRequired('finaldestinationCompanyRegionId', values)}
          as={FormCol4}
        />
        <TextField label={CompanyVatTitle} name="finaldestinationCompanyVat" />
        {!isAfisCyprus && !isMedicycle && (
          <TextField label="Δ.Ο.Υ." name="finaldestinationCompanyTaxOffice" />
        )}
      </FormRow>
      <FormRow>
        <FieldInput
          name="finalDestinationNetWeight"
          required={isFieldRequired('finalDestinationNetWeight', values)}
          as={FormCol4}
          label="Βάρος Τελικού Αποδέκτη (κιλά)"
          type="number"
        />
        <FieldInput
          name="finaldestinationCompanyNoteDocNumber"
          as={FormCol4}
          label="Αριθμός Δελτίου Ζύγισης"
        />
      </FormRow>
      <FormRow>
        <FieldInput name="finalDestinationPersonName" as={FormCol4} label="Υπεύθυνος" />
        <FieldInput name="finaldestinationCompanyEmail" as={FormCol4} label="Email" />
      </FormRow>
    </>
  );
};

type Props = {
  readonly open: boolean;
  readonly close: (result: any) => void;
  readonly title: string;
} & ModalProps;

const ModalInput = ({ finalDestinationNetWeight, open, close }: Props) => {
  return (
    <>
      <Modal size="xl" show={open} centered backdrop="static">
        <FormPage
          finalDestinationNetWeight={finalDestinationNetWeight}
          modalOnClose={(id) => {
            if (id < 0) close(false);
            close(id);
          }}
        />
      </Modal>
    </>
  );
};

export const promiseModalFinalDestInput = createModal(ModalInput);
